// src/Components/modules/Transferencias/FiltrosTraslados.js

import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Button,
  Popover,
  Checkbox,
  Form,
  Space,
  message,
  Divider,
  Typography,
} from "antd";
import {
  FilterOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  DownloadOutlined,
  CalendarOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import locale from "antd/es/date-picker/locale/es_ES";
import dayjs from "../../../utils/dayjs"; // Usar dayjs configurado con timezone
import { TRANSFER_STATUSES } from "../../../constants/trasladosStatuses";
import debounce from "lodash/debounce";

const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * Función de utilidad para convertir fechas de forma segura a ISO
 * (por si necesitas enviarlas a handleFilterChange en ese formato)
 */
const safeToISOString = (date) => {
  return date ? date.toISOString() : null;
};

/**
 * Convierte un string numérico en formato de hora válido
 * @param {string} value - El valor a convertir
 * @returns {string|null} - La hora en formato HH:mm o null si no es válido
 */
const convertToTimeFormat = (value) => {
  // Si no hay valor, retornamos null
  if (!value) return null;

  // Removemos cualquier caracter que no sea número
  const numbers = value.replace(/[^0-9]/g, "");

  // Si no hay números, retornamos null
  if (numbers.length === 0) return null;

  // Manejamos diferentes casos según la cantidad de números
  switch (numbers.length) {
    case 1: // Un solo dígito (ej: "5" -> "05:00")
      return `0${numbers}:00`;
    case 2: // Dos dígitos (ej: "17" -> "17:00")
      const hours = parseInt(numbers);
      return hours >= 0 && hours <= 23
        ? `${numbers.padStart(2, "0")}:00`
        : null;
    case 3: // Tres dígitos (ej: "530" -> "05:30")
      const h = numbers.substring(0, 1);
      const m = numbers.substring(1);
      return parseInt(h) >= 0 &&
        parseInt(h) <= 23 &&
        parseInt(m) >= 0 &&
        parseInt(m) <= 59
        ? `0${h}:${m}`
        : null;
    case 4: // Cuatro dígitos (ej: "1730" -> "17:30")
      const hh = numbers.substring(0, 2);
      const mm = numbers.substring(2);
      return parseInt(hh) >= 0 &&
        parseInt(hh) <= 23 &&
        parseInt(mm) >= 0 &&
        parseInt(mm) <= 59
        ? `${hh}:${mm}`
        : null;
    default:
      return null;
  }
};

const FiltrosTraslados = ({
  /**
   * Control de tipo de Filtro de Fecha:
   * Puede ser: "none", "week", "previousWeek", "month", "previousMonth", "custom"
   */
  dateFilterType,
  setDateFilterType,

  /** Para la lógica de flechas + hora exacta (cuando NO es "custom") */
  selectedDate,
  setSelectedDate,
  handlePrevDay,
  handleNextDay,
  singleHourFilter,
  setSingleHourFilter,

  /** Rango personalizado (cuando dateFilterType === "custom") */
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,

  /** Búsqueda */
  searchQuery,
  setSearchQuery,

  /** Otros filtros: Zona, Estado, etc. */
  zonas = [],
  selectedZona,
  setSelectedZona,
  selectedStatuses,
  setSelectedStatuses,
  selectedSeguimientoStatus,
  setSelectedSeguimientoStatus,
  selectedConfirmacionStatus,
  setSelectedConfirmacionStatus,
  selectedMovil,
  setSelectedMovil,
  //filtro de EPS
  selectedEps,
  /** Filtro de copago */
  selectedCopago,
  onCopagoFilter,

  /** Acciones */
  handleOpenDialog,
  handleFilterChange,

  //exportar excel
  onExport,

  //permisos rol
  hasPermission,

  // Estado de carga
  isLoading = false,
}) => {
  const [visiblePopover, setVisiblePopover] = useState(false);
  const [epsList, setEpsList] = useState([]);
  const [epsLoaded, setEpsLoaded] = useState(false);
  const [localEps, setLocalEps] = useState(selectedEps);

  // Función para cargar EPS (filtrado por rol del usuario)
  const fetchEps = async () => {
    try {
      // Usar endpoint que filtra por rol del usuario
      const response = await api.get("/roles/clientes/usuario");
      const { clientes, tiene_restriccion } = response.data;

      if (tiene_restriccion && clientes && clientes.length > 0) {
        // Si tiene restricción, mostrar solo los clientes permitidos
        setEpsList(clientes.map(c => ({
          id: c.id,
          nombre_cliente: c.nombre
        })));
      } else {
        // Si no tiene restricción, cargar todos los clientes
        const allClientes = await api.get("/clientes");
        setEpsList(allClientes.data);
      }
    } catch (error) {
      console.error("[FILTROS] Error al cargar EPS:", error);
      // Fallback: cargar todos los clientes
      try {
        const fallback = await api.get("/clientes");
        setEpsList(fallback.data);
      } catch (e) {
        console.error("[FILTROS] Error en fallback de EPS:", e);
      }
    }
  };

  // =============================================
  // 1. Estado local para almacenar vehículos (API)
  // =============================================
  const [vehiculos, setVehiculos] = useState([]);
  const [vehiculosCargados, setVehiculosCargados] = useState(false);

  // ========================================================
  // 2. Estado local para búsqueda con debounce (localSearch)
  // ========================================================
  const [localSearch, setLocalSearch] = useState(searchQuery);

  // Estados locales para todos los filtros
  const [localZona, setLocalZona] = useState(selectedZona);
  const [localStatuses, setLocalStatuses] = useState(selectedStatuses);
  const [localSeguimientoStatus, setLocalSeguimientoStatus] = useState(
    selectedSeguimientoStatus
  );
  const [localConfirmacionStatus, setLocalConfirmacionStatus] = useState(
    selectedConfirmacionStatus
  );
  const [localSelectedDate, setLocalSelectedDate] = useState(selectedDate);
  const [localMovil, setLocalMovil] = useState(selectedMovil);
  const [localCopago, setLocalCopago] = useState(
    selectedCopago || ["con_copago", "sin_copago"]
  );
  const [localDateFilterType, setLocalDateFilterType] =
    useState(dateFilterType);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [localStartTime, setLocalStartTime] = useState(startTime);
  const [localEndTime, setLocalEndTime] = useState(endTime);
  const [localSingleHourFilter, setLocalSingleHourFilter] =
    useState(singleHourFilter);

  // Estado para controlar si el DatePicker está abierto (evita re-renders mientras navega)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isRangePickerOpen, setIsRangePickerOpen] = useState(false);

  // Estado para controlar el mes visible en el panel del DatePicker (evita reset al navegar)
  const [datePickerPanelDate, setDatePickerPanelDate] = useState(
    localSelectedDate ? dayjs(localSelectedDate) : dayjs()
  );

  // Estado para controlar el mes visible en el RangePicker
  const [rangePickerPanelDate, setRangePickerPanelDate] = useState(dayjs());

  // =========================================================
  // 3. Función para cargar vehículos (sólo cuando se despliega)
  // =========================================================
  const fetchVehiculos = async () => {
    try {
      const response = await api.get("/vehiculos/codigo-placa");
      setVehiculos(response.data);
    } catch (error) {
      console.error("Error al obtener los vehículos:", error);
    }
  };

  const handleMovilFilterOpen = () => {
    if (!vehiculosCargados) {
      fetchVehiculos();
      setVehiculosCargados(true);
    }
  };

  // ==========================================
  // 4. Manejo de estados múltiples (checkbox)
  // ==========================================
  //  const handleStatusChange = (checkedValues) => {
  //  setSelectedStatuses(checkedValues);
  //};

  // ==========================================
  // 5. Funciones locales para botones de fecha (sin auto-aplicar filtros)
  // ==========================================
  const handleLocalPrevDay = () => {
    if (localSelectedDate) {
      const prevDay = new Date(localSelectedDate);
      prevDay.setDate(prevDay.getDate() - 1);
      setLocalSelectedDate(prevDay);
    } else {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      setLocalSelectedDate(yesterday);
    }
  };

  const handleLocalNextDay = () => {
    if (localSelectedDate) {
      const nextDay = new Date(localSelectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      setLocalSelectedDate(nextDay);
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setLocalSelectedDate(tomorrow);
    }
  };

  // ======================================================
  // 6. Debounced filter change para evitar consultas duplicadas
  // ======================================================
  const debouncedHandleFilterChange = useMemo(
    () =>
      debounce(
        (filters) => {
          if (typeof handleFilterChange === "function") {
            handleFilterChange(filters);
          }
        },
        500,
        { leading: false, trailing: true }
      ),
    [handleFilterChange]
  );

  const filtersState = useMemo(
    () => ({
      startDate: safeToISOString(startDate),
      endDate: safeToISOString(endDate),
      startTime: safeToISOString(startTime),
      endTime: safeToISOString(endTime),
      singleHourFilter,
      dateFilterType,
      searchQuery,
      selectedZona,
      selectedStatuses,
      selectedSeguimientoStatus,
      selectedConfirmacionStatus,
      selectedMovil,
      selectedEps,
    }),
    [
      startDate,
      endDate,
      startTime,
      endTime,
      singleHourFilter,
      dateFilterType,
      searchQuery,
      selectedZona,
      selectedStatuses,
      selectedSeguimientoStatus,
      selectedConfirmacionStatus,
      selectedMovil,
      selectedEps,
    ]
  );

  // Ref para rastrear el estado anterior y evitar cambios redundantes
  const previousFiltersState = useRef(null);
  const mountTimeout = useRef(null);
  const isMounted = useRef(false);

  useEffect(() => {
    // Inicializar estado anterior con el estado actual para evitar falsos cambios
    previousFiltersState.current = JSON.stringify(filtersState);

    // Deshabilitar filtros automáticos por más tiempo para estabilización completa
    mountTimeout.current = setTimeout(() => {
      isMounted.current = true;
    }, 2000); // Aumentado a 2 segundos para mayor estabilidad

    return () => {
      if (mountTimeout.current) {
        clearTimeout(mountTimeout.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Solo ejecutar si el componente está completamente montado
    if (!isMounted.current) {
      return;
    }

    // No ejecutar mientras el DatePicker o RangePicker están abiertos (evita re-renders)
    if (isDatePickerOpen || isRangePickerOpen) {
      return;
    }

    // Verificar si realmente cambió comparando con el estado anterior
    const currentStateString = JSON.stringify(filtersState);
    if (previousFiltersState.current === currentStateString) {
      return;
    }

    previousFiltersState.current = currentStateString;
    debouncedHandleFilterChange(filtersState);
  }, [filtersState, debouncedHandleFilterChange, isDatePickerOpen, isRangePickerOpen]);

  // =============================================================================
  // 7. Al cambiar dateFilterType, reseteamos el rango personalizado:
  //    Solo reseteamos cuando cambiamos DE "custom" A otro tipo (no al revés)
  // =============================================================================
  const previousDateFilterType = useRef(dateFilterType);

  useEffect(() => {
    // Solo resetear fechas si:
    // 1. Cambiamos DE "custom" A otro tipo
    // 2. O cambiamos A "custom" desde otro tipo (para limpiar y esperar nueva selección)
    const wasCustom = previousDateFilterType.current === "custom";
    const isCustom = dateFilterType === "custom";
    const typeChanged = previousDateFilterType.current !== dateFilterType;

    if (typeChanged) {
      // Si cambiamos DE custom a otro tipo, limpiar fechas custom
      if (wasCustom && !isCustom) {
        setStartDate(null);
        setEndDate(null);
        setLocalStartDate(null);
        setLocalEndDate(null);
      }

      // Si cambiamos A custom, limpiar hora exacta
      if (isCustom) {
        setSingleHourFilter(null);
        setLocalSingleHourFilter(null);
      }

      // Limpiar tiempos solo si cambiamos de tipo
      setStartTime(null);
      setEndTime(null);

      previousDateFilterType.current = dateFilterType;
    }
  }, [
    dateFilterType,
    setStartDate,
    setEndDate,
    setStartTime,
    setEndTime,
    setSingleHourFilter,
  ]);

  // ===========================================================================
  // 8. Función para manejar el cambio de RangePicker en el rango personalizado
  //    Aquí se valida que NO exceda los 3 meses (igual que en Filters.js)
  // ===========================================================================
  const handleCustomDateChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      // Validar que no exceda 3 meses
      const diffMonths = end.diff(start, "month", true);
      if (diffMonths > 3) {
        message.error("El rango no puede exceder los 3 meses");
        return;
      }
      // Asigna la fecha de inicio ajustada al inicio del día
      const startDate = start.toDate();
      startDate.setHours(0, 0, 0, 0);
      setLocalStartDate(startDate);
      setStartDate(startDate); // Actualizar estado global inmediatamente

      // Asigna la fecha final ajustada al final del día
      const endDate = end.toDate();
      endDate.setHours(23, 59, 59, 999);
      setLocalEndDate(endDate);
      setEndDate(endDate); // Actualizar estado global inmediatamente

      // Aplicar filtros automáticamente
      handleApplyFilters();
    } else {
      // Si se limpia, ponemos null
      setLocalStartDate(null);
      setLocalEndDate(null);
      setStartDate(null);
      setEndDate(null);
    }
  };

  // ==========================================
  // 9. Popover para los estados múltiples
  // ==========================================
  const popoverContent = (
    <div>
      <Space direction="vertical">
        <Space>
          <Button
            size="small"
            onClick={() => {
              setLocalStatuses(TRANSFER_STATUSES);
            }}
          >
            Seleccionar T
          </Button>
          <Button
            size="small"
            onClick={() => {
              setLocalStatuses([]);
            }}
          >
            Deseleccionar T
          </Button>
        </Space>
        <Checkbox.Group
          options={TRANSFER_STATUSES}
          value={localStatuses}
          onChange={setLocalStatuses}
          style={{ display: "flex", flexDirection: "column" }}
        />
        <Divider style={{ margin: "8px 0" }} />
        <Typography.Text strong>Filtro por Copago:</Typography.Text>
        <Checkbox.Group
          options={[
            { label: "Con Copago", value: "con_copago" },
            { label: "Sin Copago", value: "sin_copago" },
          ]}
          value={localCopago}
          onChange={setLocalCopago}
          style={{ display: "flex", flexDirection: "column" }}
        />
      </Space>
    </div>
  );

  // Función para aplicar todos los filtros
  const handleApplyFilters = async () => {
    try {
      // Actualizar todos los estados globales
      setSearchQuery(localSearch);
      setSelectedZona(localZona);
      setSelectedStatuses(localStatuses);
      setSelectedSeguimientoStatus(localSeguimientoStatus);
      setSelectedConfirmacionStatus(localConfirmacionStatus);
      setSelectedMovil(localMovil);
      onCopagoFilter(localCopago);
      setDateFilterType(localDateFilterType);
      setSelectedDate(localSelectedDate);

      // Aplicar las fechas ajustadas
      if (localStartDate) {
        const startDate = new Date(localStartDate);
        startDate.setHours(0, 0, 0, 0);
        setStartDate(startDate);
      } else {
        setStartDate(null);
      }

      if (localEndDate) {
        const endDate = new Date(localEndDate);
        endDate.setHours(23, 59, 59, 999);
        setEndDate(endDate);
      } else {
        setEndDate(null);
      }

      setStartTime(localStartTime);
      setEndTime(localEndTime);
      setSingleHourFilter(localSingleHourFilter);

      // Llamar a handleFilterChange con todos los filtros actuales
      if (typeof handleFilterChange === "function") {
        const filters = {
          startDate: localStartDate ? safeToISOString(localStartDate) : null,
          endDate: localEndDate ? safeToISOString(localEndDate) : null,
          startTime: safeToISOString(localStartTime),
          endTime: safeToISOString(localEndTime),
          singleHourFilter: localSingleHourFilter,
          dateFilterType: localDateFilterType,
          searchQuery: localSearch,
          selectedZona: localZona,
          selectedStatuses: localStatuses,
          selectedSeguimientoStatus: localSeguimientoStatus,
          selectedConfirmacionStatus: localConfirmacionStatus,
          selectedMovil: localMovil,
          selectedCopago: localCopago,
          selectedEps: localEps,
        };
        await handleFilterChange(filters);
      }
    } catch (error) {
      console.error("Error al aplicar filtros:", error);
      message.error("Error al aplicar filtros");
    }
  };

  // Función para limpiar todos los filtros
  const handleClearFilters = () => {
    // Restablecer estados locales a sus valores iniciales
    setLocalSearch("");
    setLocalZona("");
    setLocalStatuses(TRANSFER_STATUSES);
    setLocalSeguimientoStatus("Ambos");
    setLocalConfirmacionStatus("Ambos");
    setLocalMovil(null);
    setLocalCopago(["con_copago", "sin_copago"]);
    setLocalDateFilterType("none");
    setLocalSingleHourFilter(null);
    setLocalStartDate(null);
    setLocalEndDate(null);
    setLocalStartTime(null);
    setLocalEndTime(null);
    setLocalSelectedDate(null);

    setLocalEps("");

    // Aplicar los cambios inmediatamente
    setSearchQuery("");
    setSelectedZona("");
    setSelectedStatuses(TRANSFER_STATUSES);
    setSelectedSeguimientoStatus("Ambos");
    setSelectedConfirmacionStatus("Ambos");
    setSelectedMovil(null);
    onCopagoFilter(["con_copago", "sin_copago"]);
    setDateFilterType("none");
    setStartDate(null);
    setEndDate(null);
    setStartTime(null);
    setEndTime(null);
    setSingleHourFilter(null);

    // Llamar a handleFilterChange con los valores iniciales
    if (typeof handleFilterChange === "function") {
      handleFilterChange({
        startDate: null,
        endDate: null,
        startTime: null,
        endTime: null,
        dateFilterType: "none",
        searchQuery: "",
        selectedZona: "",
        selectedStatuses: TRANSFER_STATUSES,
        selectedSeguimientoStatus: "Ambos",
        selectedConfirmacionStatus: "Ambos",
        selectedMovil: null,
        selectedCopago: ["con_copago", "sin_copago"],
        selectedEps: "",
      });
    }
  };

  return (
    <Form layout="vertical">
      {/* ========================================================
          1ERA FILA: FILTROS PRINCIPALES (BÚSQUEDA, ZONA, ETC.)
      ========================================================= */}
      <Row gutter={[16, 16]} align="middle">
        {/* Buscador */}
        <Col xs={24} sm={12} md={4}>
          <Input
            placeholder="CS, Institución, Nombre, Documento, PRE, AUT"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            allowClear
            suffix={<SearchOutlined />}
            size="large"
            disabled={isLoading}
          />
        </Col>
        {/* Móvil */}
        <Col xs={24} sm={12} md={3}>
          <Select
            showSearch
            placeholder="Móvil (Código - Placa)"
            value={
              localMovil
                ? `${localMovil.codigo} - ${localMovil.placa}`
                : undefined
            }
            onChange={(value) => {
              const movil = vehiculos.find(
                (v) => `${v.codigo} - ${v.placa}` === value
              );
              setLocalMovil(movil || null);
            }}
            onOpenChange={(open) => {
              if (open) handleMovilFilterOpen();
            }}
            options={vehiculos.map((vehiculo) => ({
              label: `${vehiculo.codigo} - ${vehiculo.placa}`,
              value: `${vehiculo.codigo} - ${vehiculo.placa}`,
            }))}
            allowClear
            size="large"
            style={{ width: "100%" }}
            disabled={isLoading}
          />
        </Col>
        {/* Confirmación */}
        <Col xs={24} sm={12} md={2}>
          <Select
            placeholder="Confirmación"
            value={localConfirmacionStatus}
            onChange={setLocalConfirmacionStatus}
            style={{ width: "100%" }}
            size="large"
            disabled={isLoading}
          >
            <Option value="Ambos">Ambos</Option>
            <Option value="Confirmado">Confirmado</Option>
            <Option value="Sin Confirmar">Sin Confirmar</Option>
          </Select>
        </Col>
        {/* Seguimiento */}
        <Col xs={24} sm={12} md={2}>
          <Select
            placeholder="Seguimiento"
            value={localSeguimientoStatus}
            onChange={setLocalSeguimientoStatus}
            style={{ width: "100%" }}
            size="large"
            disabled={isLoading}
          >
            <Option value="Ambos">Ambos</Option>
            <Option value="Validado">Validado</Option>
            <Option value="Sin Validar">Sin Validar</Option>
          </Select>
        </Col>
        {/* Zona */}
        <Col xs={24} sm={12} md={3}>
          <Select
            placeholder="Zona"
            value={localZona}
            onChange={setLocalZona}
            style={{ width: "100%" }}
            size="large"
            disabled={isLoading}
          >
            <Option value="">Todas las Zonas</Option>
            {zonas.length > 0 ? (
              zonas.map((zona) => (
                <Option key={zona.id} value={zona.nombre}>
                  {zona.nombre}
                </Option>
              ))
            ) : (
              <Option value="" disabled>
                No hay zonas disponibles
              </Option>
            )}
          </Select>
        </Col>
        {/* Selector de EPS */}
        <Col xs={24} sm={12} md={3}>
          <Select
            showSearch
            placeholder="EPS"
            value={localEps}
            onChange={(value) => {
              setLocalEps(value);
            }}
            onFocus={() => {
              if (!epsLoaded) {
                fetchEps();
                setEpsLoaded(true);
              }
            }}
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            allowClear
            size="large"
            style={{ width: "100%" }}
            disabled={isLoading}
          >
            <Option value="">Todas las EPS</Option>
            {epsList.map((eps) => (
              <Option key={eps.id} value={eps.nombre_cliente}>
                {eps.nombre_cliente}
              </Option>
            ))}
          </Select>
        </Col>
        {/* Select para Tipo de Fecha: none, week, month, custom, etc. */}
        <Col xs={24} sm={12} md={3}>
          <Select
            placeholder="Filtro de Fechas"
            value={localDateFilterType}
            onChange={(value) => setLocalDateFilterType(value)}
            allowClear={false}
            style={{ width: "100%" }}
            size="large"
            disabled={isLoading}
          >
            <Option value="none">Sin Rango</Option>
            <Option value="week">Semana Actual</Option>
            <Option value="previousWeek">Semana Anterior</Option>
            <Option value="month">Mes Actual</Option>
            <Option value="previousMonth">Mes Anterior</Option>
            {/* Aquí la opción de "Rango Personalizado" */}
            <Option value="custom">Rango Personalizado</Option>
          </Select>
        </Col>
        {/* Hora Exacta (ahora en la primera fila) */}
        <Col xs={24} sm={12} md={2}>
          <TimePicker
            placeholder="Hora Exacta"
            value={localSingleHourFilter ? dayjs(localSingleHourFilter) : null}
            onChange={(time) =>
              setLocalSingleHourFilter(time ? time.toDate() : null)
            }
            format="HH:mm"
            style={{ width: "100%" }}
            allowClear
            inputReadOnly={false}
            size="large"
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const timeStr = convertToTimeFormat(e.target.value);
                if (timeStr) {
                  const [hours, minutes] = timeStr.split(":");
                  const newDate = new Date();
                  newDate.setHours(parseInt(hours));
                  newDate.setMinutes(parseInt(minutes));
                  setLocalSingleHourFilter(newDate);
                }
              }
            }}
          />
        </Col>
        {/* Popover de Estados múltiples */}
        <Col xs={24} sm={6} md={2}>
          <Popover
            content={popoverContent}
            title="Estados"
            trigger="click"
            open={visiblePopover}
            onOpenChange={(visible) => setVisiblePopover(visible)}
          >
            <Button
              icon={<FilterOutlined />}
              size="large"
              style={{ width: "100%" }}
              aria-label="Filtros de Estados"
              disabled={isLoading}
            />
          </Popover>
        </Col>
      </Row>

      {/* ===============================================
          2DA FILA: BOTONES Y CONTROL DE FECHAS/HORAS
      =============================================== */}
      <Row gutter={[16, 16]} align="middle" style={{ marginTop: 16 }}>
        {/* Izquierda: Limpiar y Aplicar Filtros */}
        <Col xs={24} md={8} style={{ textAlign: "left" }}>
          <Space>
            <Button
              onClick={handleClearFilters}
              size="large"
              icon={<ClearOutlined />}
              disabled={isLoading}
            >
              Limpiar Filtros
            </Button>
            <Button
              type="primary"
              onClick={handleApplyFilters}
              size="large"
              icon={<SearchOutlined />}
              disabled={isLoading}
            >
              Aplicar Filtros
            </Button>
          </Space>
        </Col>
        {/* Centro: Selector de días solo si Sin Rango */}
        <Col xs={24} md={8} style={{ textAlign: "center" }}>
          {localDateFilterType === "none" && (
            <Space size="middle">
              <Button
                icon={<LeftOutlined />}
                onClick={handleLocalPrevDay}
                size="large"
                disabled={isLoading}
              />
              <DatePicker
                value={localSelectedDate ? dayjs(localSelectedDate) : null}
                onChange={(date) => {
                  setLocalSelectedDate(date ? date.toDate() : null);
                  if (date) {
                    setDatePickerPanelDate(date);
                  }
                }}
                format="DD/MM/YYYY"
                locale={locale}
                allowClear={false}
                style={{ width: "130px", textAlign: "center" }}
                size="large"
                disabled={isLoading}
                getPopupContainer={(trigger) => trigger.parentElement}
                open={isDatePickerOpen}
                pickerValue={datePickerPanelDate}
                onPanelChange={(date) => {
                  setDatePickerPanelDate(date);
                }}
                onOpenChange={(open) => {
                  setIsDatePickerOpen(open);
                  // Cuando se abre, sincronizar con la fecha seleccionada
                  if (open && localSelectedDate) {
                    setDatePickerPanelDate(dayjs(localSelectedDate));
                  }
                }}
              />
              <Button
                icon={<RightOutlined />}
                onClick={handleLocalNextDay}
                size="large"
                disabled={isLoading}
              />
            </Space>
          )}
        </Col>
        {/* Derecha: Programar y Exportar Traslados */}
        <Col xs={24} md={8} style={{ textAlign: "right" }}>
          <Space>
            {hasPermission("PROGRAMAR_CC") && (
              <Button
                type="primary"
                onClick={handleOpenDialog}
                size="large"
                icon={<CalendarOutlined />}
                disabled={isLoading}
              >
                Programar
              </Button>
            )}
            {hasPermission("VISUALIZAR_CC") && (
              <Button
                type="primary"
                onClick={onExport}
                icon={<DownloadOutlined />}
                size="large"
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                disabled={isLoading}
              ></Button>
            )}
          </Space>
        </Col>
      </Row>
      {/* Si el filtro es personalizado, muestra el RangePicker debajo */}
      {localDateFilterType === "custom" && (
        <Row gutter={[16, 16]} align="middle" style={{ marginTop: 16 }}>
          <Col xs={24} sm={24} md={6}>
            <RangePicker
              locale={locale}
              placeholder={["Fecha Inicio", "Fecha Fin"]}
              value={
                localStartDate && localEndDate
                  ? [dayjs(localStartDate), dayjs(localEndDate)]
                  : undefined
              }
              onChange={handleCustomDateChange}
              allowClear
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
              disabled={isLoading}
              getPopupContainer={(trigger) => trigger.parentElement}
              open={isRangePickerOpen}
              pickerValue={rangePickerPanelDate}
              onPanelChange={(date) => {
                if (date) {
                  setRangePickerPanelDate(date);
                }
              }}
              onOpenChange={(open) => {
                setIsRangePickerOpen(open);
                // Cuando se abre, sincronizar con la fecha de inicio o fecha actual
                if (open) {
                  setRangePickerPanelDate(localStartDate ? dayjs(localStartDate) : dayjs());
                }
              }}
            />
          </Col>
        </Row>
      )}
    </Form>
  );
};

/**
 * ==================================================
 * Validación de PropTypes, ajusta según tu necesidad
 * ==================================================
 */
FiltrosTraslados.propTypes = {
  dateFilterType: PropTypes.string.isRequired,
  setDateFilterType: PropTypes.func.isRequired,

  // Control de flechas + hora (cuando no es "custom")
  selectedDate: PropTypes.instanceOf(Date),
  setSelectedDate: PropTypes.func.isRequired,
  handlePrevDay: PropTypes.func.isRequired,
  handleNextDay: PropTypes.func.isRequired,
  singleHourFilter: PropTypes.instanceOf(Date),
  setSingleHourFilter: PropTypes.func.isRequired,

  // Rango personalizado
  startDate: PropTypes.instanceOf(Date),
  setStartDate: PropTypes.func.isRequired,
  endDate: PropTypes.instanceOf(Date),
  setEndDate: PropTypes.func.isRequired,
  startTime: PropTypes.instanceOf(Date),
  setStartTime: PropTypes.func.isRequired,
  endTime: PropTypes.instanceOf(Date),
  setEndTime: PropTypes.func.isRequired,

  // Búsqueda
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,

  // Otros filtros
  zonas: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedZona: PropTypes.string.isRequired,
  setSelectedZona: PropTypes.func.isRequired,
  selectedStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedStatuses: PropTypes.func.isRequired,
  selectedSeguimientoStatus: PropTypes.string.isRequired,
  setSelectedSeguimientoStatus: PropTypes.func.isRequired,
  selectedConfirmacionStatus: PropTypes.string.isRequired,
  setSelectedConfirmacionStatus: PropTypes.func.isRequired,
  selectedMovil: PropTypes.object,
  setSelectedMovil: PropTypes.func.isRequired,

  // Botón Programar
  handleOpenDialog: PropTypes.func.isRequired,

  // Para notificar cambios globales
  handleFilterChange: PropTypes.func,

  // Filtro de copago
  selectedCopago: PropTypes.array,
  onCopagoFilter: PropTypes.func.isRequired,

  //Exportar Excel
  onExport: PropTypes.func.isRequired,

  // Permisos de rol
  hasPermission: PropTypes.func.isRequired,

  // Estado de carga
  isLoading: PropTypes.bool,
};

export default FiltrosTraslados;
